import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
export const COIN_FLIGHT_TIMING = {
    coins: 4,
    staggerMs: 110,
    flightMs: 750,
    taper: 0.7,
    easing: 'cubic-bezier(0.45, 0, 0.3, 1)',
    arrivalFraction: 0.86,
};
/**
 * A SPEND's coins (WP-110.3): the SAME trail as a reward's — 4 coins, the same
 * gaps and trip — reversed, pill → the paid button. The first version (3 coins,
 * 90 ms apart, 560 ms trips) was not noticeable (user, 2026-10-10: "very fast
 * or having less coins as compared to trails used to credit coins").
 */
export const SPEND_FLIGHT_TIMING = { ...COIN_FLIGHT_TIMING, coins: 4 };
/**
 * The size a spend's coins land at on the paid button's face: larger than the
 * pill coin they leave, so the trail grows toward the button and stays visible
 * against the board it crosses (never larger than the button's own face).
 * In HUD px: multiplied by the target's zoom (Module 121, D5) — see
 * {@link spendLandingSize}.
 */
export const SPEND_LANDING_SIZE = 34;
/** A spend's landing size on `to`, in screen px: {@link SPEND_LANDING_SIZE} at `to`'s zoom, never past its face. */
export function spendLandingSize(to) {
    return Math.min(to.size, SPEND_LANDING_SIZE * (to.scale ?? 1));
}
/** How many coins a reward of `amount` sends (F): small stays quiet, big reads big. */
export function coinCountFor(amount) {
    return amount < 20 ? 3 : 4;
}
/** The default timing for a flight carrying `amount`. */
export function coinFlightTimingFor(amount, base = COIN_FLIGHT_TIMING) {
    return { ...base, coins: coinCountFor(amount) };
}
/** When coin `i` lands, from the start of the flight. */
export function coinLandAt(i, timing = COIN_FLIGHT_TIMING) {
    return i * timing.staggerMs + timing.flightMs * (timing.arrivalFraction ?? 1);
}
/**
 * The drawn size of a flying coin, from the source anchor's size: 18–40 HUD px,
 * at the source's zoom (Module 121, D5 — fixed screen px drew phone-sized coins
 * on an iPad).
 */
export function coinFlightSize(from) {
    const s = from.scale ?? 1;
    return Math.max(18 * s, Math.min(from.size, 40 * s));
}
/**
 * Coin `i`'s keyframes: one shared quadratic curve from the source's centre to
 * the pill, bowing away from the nearer screen edge so it never leaves the
 * screen. Pure (no DOM), so the preview page draws exactly what the game does.
 */
export function coinKeyframes(i, timing, from, to, viewportWidth) {
    const size = coinFlightSize(from);
    const n = Math.max(1, timing.coins);
    const tail = n === 1 ? 1 : 1 - ((1 - timing.taper) * i) / (n - 1);
    // Exactly the target's size on arrival: the coin must sit ON the pill's coin.
    const endScale = to.size / size;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.hypot(dx, dy) || 1;
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    // Perpendicular to the straight line, a fifth of its length.
    let px = -dy / dist;
    let py = dx / dist;
    // Bow toward the middle of the screen: right when the path is on the left
    // half, left when it is on the right half.
    const towardRight = midX < viewportWidth / 2;
    if (towardRight ? px < 0 : px > 0) {
        px = -px;
        py = -py;
    }
    const cx = midX + px * dist * 0.2;
    const cy = midY + py * dist * 0.2;
    const steps = 12;
    const frames = [];
    for (let k = 0; k <= steps; k += 1) {
        const t = k / steps;
        const x = (1 - t) ** 2 * from.x + 2 * (1 - t) * t * cx + t ** 2 * to.x;
        const y = (1 - t) ** 2 * from.y + 2 * (1 - t) * t * cy + t ** 2 * to.y;
        // The taper is a mid-air bulge (0 at both ends): every coin leaves at the
        // source's size and lands at the target's.
        const shrink = 1 - (1 - tail) * Math.sin(Math.PI * t);
        const scale = (1 + (endScale - 1) * t) * shrink;
        // Fade in over the first stretch (out of the source coin, not popping into
        // existence on top of it) and out over the last (into the target coin).
        const opacity = t < 0.12 ? (t / 0.12) * shrink : t > 0.88 ? ((1 - t) / 0.12) * shrink : shrink;
        frames.push({
            transform: `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${scale})`,
            opacity,
            offset: t,
        });
    }
    return frames;
}
export function CoinFlight({ from, to, iconUrl, timing = COIN_FLIGHT_TIMING, onLeave, onLand, onDone, }) {
    const layerRef = useRef(null);
    // The callbacks may change identity on every render of the owner; the
    // schedule is set once per flight.
    const handlers = useRef({ onLeave, onLand, onDone });
    handlers.current = { onLeave, onLand, onDone };
    useEffect(() => {
        const timers = [];
        const layer = layerRef.current;
        const halo = layer?.querySelector('.coin-flight__halo') ?? null;
        for (let i = 0; i < timing.coins; i += 1) {
            timers.push(setTimeout(() => handlers.current.onLeave?.(i), i * timing.staggerMs));
            timers.push(setTimeout(() => {
                // The landing's effect: a soft gold glow behind the coin, growing and
                // fading. The coin itself never changes size.
                halo?.animate?.([
                    { opacity: 0, transform: 'translate(-50%, -50%) scale(0.7)' },
                    { opacity: 0.85, transform: 'translate(-50%, -50%) scale(1.15)', offset: 0.3 },
                    { opacity: 0, transform: 'translate(-50%, -50%) scale(1.5)' },
                ], { duration: 420, easing: 'ease-out' });
                handlers.current.onLand(i);
            }, coinLandAt(i, timing)));
        }
        // Done once the last coin has finished its trip (and faded), not at its
        // arrival: the layer must outlive the fade.
        timers.push(setTimeout(() => handlers.current.onDone(), (timing.coins - 1) * timing.staggerMs + timing.flightMs + 40));
        const vw = globalThis.innerWidth || 0;
        Array.from(layer?.querySelectorAll('.coin-flight__coin') ?? []).forEach((el, i) => {
            if (!(el instanceof HTMLElement) || typeof el.animate !== 'function')
                return;
            el.animate(coinKeyframes(i, timing, from, to, vw), {
                duration: timing.flightMs,
                delay: i * timing.staggerMs,
                easing: timing.easing,
                // Hidden before it leaves; GONE after it lands (it has faded into the
                // target) — never held on top of the pill.
                fill: 'backwards',
            });
        });
        return () => {
            for (const t of timers)
                clearTimeout(t);
        };
        // One flight per mount: the owner keys this component per flight.
    }, []);
    if (typeof document === 'undefined')
        return null;
    const size = coinFlightSize(from);
    return createPortal(_jsxs("div", { className: "coin-flight", ref: layerRef, "aria-hidden": "true", "data-testid": "coin-flight", children: [_jsx("span", { className: "coin-flight__halo", style: { left: to.x, top: to.y, width: to.size * 2.2, height: to.size * 2.2 } }), Array.from({ length: timing.coins }, (_, i) => iconUrl ? (_jsx("img", { className: "coin-flight__coin", src: iconUrl, alt: "", style: { width: size, height: size } }, i)) : (_jsx("span", { className: "coin-flight__coin coin-flight__coin--drawn", style: { width: size, height: size } }, i)))] }), document.body);
}
